/** База игроков: поиск, отбор, сортировка, правка анкет, выгрузка. */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  hasCapability,
  tierLabel,
  TIERS,
  type CaptureActivity,
  type PlayerCombat,
  type PlayerStats,
  type SessionUser,
  type Tier,
} from '@ak/shared';

import { apiDelete, apiGet, apiPatch, apiPost } from '../lib/api.ts';
import { formatCount, formatDateTime, formatRelative, formatShortDate } from '../lib/format.ts';
import {
  EMPTY_FILTERS,
  filterPlayers,
  isFilterActive,
  type PlayerActivity,
  type PlayerFilters,
  type PlayerProblem,
  type PlayerStatus,
} from '../lib/player-filter.ts';
import { currentPlayerView, playerViewToQuery } from '../lib/player-query.ts';
import { comparePlayers, type PlayerRow, type PlayerSortKey } from '../lib/player-sort.ts';
import { navigate } from '../lib/router.ts';
import { TierPicker } from '../components/tier-picker.tsx';
import { useResource } from '../lib/useResource.ts';
import { useToast } from '../components/toast.tsx';
import {
  Badge,
  Button,
  Checkbox,
  EmptyState,
  ErrorState,
  Field,
  FilterChip,
  Modal,
  PageHeader,
  Panel,
  SegmentedControl,
  Skeleton,
  Spinner,
  TextInput,
  TierBadge,
  tierColor,
} from '../components/ui.tsx';

/** Кто ходит с тир-ролью, но без анкеты: живой список, а не выборка из базы. */
interface StraysResponse {
  members: {
    userId: string;
    discordName: string;
    avatarUrl: string;
    joinedAt: string | null;
    tier: Tier;
  }[];
  /** Сколько участников сервера просмотрено — по нему видно охват проверки. */
  checked: number;
  withProfile: number;
}

/**
 * Кого показывать в списке.
 *
 * Не галка, а выбор из трёх: чаще всего нужен либо обычный список, либо
 * только чепушилы — разбирать их удобнее, когда никого другого на экране нет.
 */
type ProfileScope = 'with' | 'without' | 'all';

interface PlayersResponse {
  players: PlayerStats[];
  /** Боевые цифры по идентификатору Discord. Ключа нет — каптов не было. */
  combat: Record<string, PlayerCombat>;
  /** Сколько каптов было у семьи за те же окна — знаменатель к цифрам игрока. */
  activity: CaptureActivity;
  /** Когда тиры последний раз сверялись с ролями Discord. */
  tiersCheckedAt: string | null;
  botReady: boolean;
}

/** Что нужно колонке помимо самой строки. */
interface ColumnContext {
  activity: CaptureActivity;
  timezone: string;
}

/**
 * Колонка, которую можно включить или выключить.
 *
 * Постоянных колонок здесь нет: тир, ник и статик стоят в таблице всегда и
 * рисуются по-своему — со значком тира и пометкой «вышел». Всё остальное
 * складывается из чисел, поэтому описывается одинаково: подпись, группа и
 * способ достать значение. Тем же способом строится и выгрузка.
 */
interface ColumnDef {
  key: PlayerSortKey;
  label: string;
  hint: string;
  group: string;
  value: (row: PlayerRow, context: ColumnContext) => string;
}

function formatNumber(value: number, digits = 0): string {
  return value.toLocaleString('ru-RU', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/** Значение боевой колонки. Прочерк — человек не был ни на одном капте. */
function combatValue(row: PlayerRow, read: (combat: PlayerCombat) => string): string {
  return row.combat === null ? '—' : read(row.combat);
}

const OPTIONAL_COLUMNS: ColumnDef[] = [
  {
    key: 'mainCount',
    label: 'Основа',
    hint: 'Сколько раз человек попал в основной состав набора',
    group: 'Наборы',
    value: (row) => String(row.mainCount),
  },
  {
    key: 'reserveCount',
    label: 'Замена',
    hint: 'Сколько раз остался в замене',
    group: 'Наборы',
    value: (row) => String(row.reserveCount),
  },
  {
    key: 'mclVzzCount',
    label: 'MCL+VZZ',
    hint: 'Наборы типов MCL и VZZ в основном составе',
    group: 'Наборы',
    value: (row) => String(row.mclVzzCount),
  },
  {
    key: 'captureCount',
    label: 'CAPTURE',
    hint: 'Наборы типа CAPTURE в основном составе — записи в наборе, а не сами капты',
    group: 'Наборы',
    value: (row) => String(row.captureCount),
  },
  {
    key: 'captures',
    label: 'Каптов',
    hint: 'Каптов по данным игровой службы — настоящие капты, а не записи в набор',
    group: 'Капты',
    value: (row) => combatValue(row, (combat) => String(combat.captures)),
  },
  {
    key: 'kd',
    label: 'K/D',
    hint: 'Убийства к смертям по данным vzp-gta5rp.com',
    group: 'Капты',
    value: (row) => combatValue(row, (combat) => (combat.kd === null ? '—' : formatNumber(combat.kd, 2))),
  },
  {
    key: 'avgDamage',
    label: 'Урон/капт',
    hint: 'Средний урон за капт',
    group: 'Капты',
    value: (row) => combatValue(row, (combat) => formatNumber(combat.avgDamage)),
  },
  {
    key: 'avgKills',
    label: 'Киллы/капт',
    hint: 'Среднее число киллов за капт',
    group: 'Капты',
    value: (row) => combatValue(row, (combat) => formatNumber(combat.avgKills, 2)),
  },
  {
    key: 'damage',
    label: 'Урон',
    hint: 'Весь урон за всё время',
    group: 'Капты',
    value: (row) => combatValue(row, (combat) => formatNumber(combat.damage)),
  },
  {
    key: 'kills',
    label: 'Киллы',
    hint: 'Все киллы за всё время',
    group: 'Капты',
    value: (row) => combatValue(row, (combat) => String(combat.kills)),
  },
  {
    key: 'wins',
    label: 'Побед',
    hint: 'Каптов, выигранных семьёй, из тех, где человек был',
    group: 'Капты',
    value: (row) => combatValue(row, (combat) => `${String(combat.wins)}/${String(combat.wins + combat.losses)}`),
  },
  {
    key: 'capturesMonth',
    label: 'За 30 дней',
    hint: 'Сколько каптов из прошедших за месяц человек отыграл',
    group: 'Капты',
    value: (row, context) =>
      combatValue(row, (combat) => `${String(combat.capturesMonth)} из ${String(context.activity.month)}`),
  },
  {
    key: 'lastCaptureAt',
    label: 'Последний капт',
    hint: 'Когда человек последний раз был на капте',
    group: 'Капты',
    value: (row, context) => combatValue(row, (combat) => formatShortDate(combat.lastCaptureAt, context.timezone)),
  },
];

/**
 * Колонки, включённые до первой настройки.
 *
 * Не все сразу: строка со всеми числами не помещается на экран, а список,
 * который надо листать вбок, читают хуже. Здесь по одной из каждой группы —
 * дальше человек включает своё.
 */
const DEFAULT_COLUMNS: PlayerSortKey[] = ['mainCount', 'captures', 'kd', 'avgDamage', 'avgKills', 'lastCaptureAt'];

const COLUMNS_STORAGE_KEY = 'ak.players.columns';

function readStoredColumns(): PlayerSortKey[] {
  // Хранилища может не быть вовсе: этим же кодом страница отрисовывается в
  // дымовых тестах, где никакого браузера вокруг нет.
  if (typeof window === 'undefined') {
    return DEFAULT_COLUMNS;
  }

  try {
    const raw = window.localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (raw === null) {
      return DEFAULT_COLUMNS;
    }

    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return DEFAULT_COLUMNS;
    }

    // Неизвестные ключи выбрасываются: колонку могли переименовать или убрать,
    // и старый выбор не должен ронять страницу.
    const known = new Set<string>(OPTIONAL_COLUMNS.map((column) => column.key));
    return parsed.filter((key): key is PlayerSortKey => typeof key === 'string' && known.has(key));
  } catch {
    // Хранилище может быть недоступно — в приватном окне это не беда.
    return DEFAULT_COLUMNS;
  }
}

function storeColumns(keys: readonly PlayerSortKey[]): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(keys));
  } catch {
    // Не сохранилось — переживём: выбор колонок не стоит сообщения об ошибке.
  }
}

/**
 * Метка порядка байтов в начале выгрузки.
 *
 * Без неё Excel открывает кириллицу мусором. Кодом, а не знаком в
 * тексте: сам знак невидим, и в исходном файле его не отличить от пробела.
 */
const BYTE_ORDER_MARK = String.fromCharCode(0xfeff);

/** Значение ячейки CSV: кавычки удваиваются, разделители прячутся в кавычки. */
function csvCell(value: string): string {
  return /[",\n;]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/**
 * Выгружает то, что видно на экране: отобранные строки и включённые колонки.
 *
 * Собирается в браузере, а не на сервере: серверная выгрузка не знает ни про
 * фильтры, ни про выбор колонок, и повторять их в запросе значило бы держать
 * отбор в двух местах. Полная база по-прежнему лежит по адресу
 * `/api/players/export.csv`.
 */
function downloadCsv(rows: readonly PlayerRow[], columns: readonly ColumnDef[], context: ColumnContext): void {
  // Идентификатор остаётся: имя в Discord человек может сменить, а он — нет.
  const header = ['discordID', 'Discord', 'Тир', 'Ник', 'Статик', ...columns.map((column) => column.label)];
  const lines = rows.map((row) => [
    row.userId,
    row.discordName ?? '',
    // Здесь номер, а не подпись тира, хотя на экранах у четвёртого «plus»:
    // колонку сортируют и фильтруют в Excel как числа, а слово сделало бы её
    // смешанной и сломало бы сортировку у всех, кто уже пользуется файлом.
    row.tier === 0 ? '' : String(row.tier),
    row.gameNick,
    row.staticId,
    ...columns.map((column) => column.value(row, context)),
  ]);

  const csv = [header, ...lines].map((line) => line.map(csvCell).join(',')).join('\n');
  // Метка порядка байтов нужна, иначе Excel открывает кириллицу мусором.
  const blob = new Blob([`${BYTE_ORDER_MARK}${csv}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = 'players.csv';
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Ширина одной ячейки отбора.
 *
 * Кнопки тира, пометки «разобрать» и поля порогов стоят в одном ряду, поэтому
 * ширину им задаёт общая обёртка: иначе каждая подпись растягивала бы ячейку
 * по себе и ряд шёл бы уступами.
 *
 * Замерено в живой админке: внутри ячейки за вычетом отступов кнопки остаётся
 * 98 px, и в них укладываются все подписи — самая длинная, «Бот не видел»,
 * занимает 90 px. Подпись длиннее переносится на вторую строку, и кнопка
 * становится выше соседей; на этом уже наступали с «Не виден боту».
 */
const FILTER_CELL = 'w-32';

// Подписи — из общего словаря, «без тира» дописано руками: слово для нуля на
// каждом экране своё. Значения остаются числами: по ним идёт отбор и
// параметр адреса `?tiers=4`.
const TIER_OPTIONS: { value: Tier; label: string }[] = [
  ...TIERS.map((tier) => ({ value: tier, label: tierLabel(tier) })),
  { value: 0, label: 'Без тира' },
];

const PROBLEM_OPTIONS: { value: PlayerProblem; label: string; hint: string }[] = [
  { value: 'noStatic', label: 'Без статика', hint: 'В анкете пусто — цифры к такому человеку не привяжутся' },
  { value: 'noCaptures', label: 'Без каптов', hint: 'Анкета есть, а на каптах человека не было' },
  { value: 'unseen', label: 'Бот не видел', hint: 'Бот ни разу не встречал этого человека в Discord' },
];

/** Поле порога: пустое значит «порога нет», а не ноль. */
function ThresholdInput({
  label,
  value,
  step,
  onChange,
}: {
  label: string;
  value: number | null;
  step?: string;
  onChange: (value: number | null) => void;
}): ReactNode {
  return (
    <Field label={label}>
      <TextInput
        type="number"
        min="0"
        step={step ?? '1'}
        placeholder="любой"
        value={value === null ? '' : String(value)}
        onChange={(event) => {
          const raw = event.target.value.trim();
          const parsed = Number(raw);
          onChange(raw === '' || Number.isNaN(parsed) ? null : parsed);
        }}
      />
    </Field>
  );
}

/** Правка ника и статика. Игровой ник виден в составах наборов в Discord. */
function EditPlayerModal({
  player,
  onClose,
  onSaved,
}: {
  player: PlayerRow;
  onClose: () => void;
  onSaved: () => void;
}): ReactNode {
  const toast = useToast();
  const [gameNick, setGameNick] = useState(player.gameNick);
  const [staticId, setStaticId] = useState(player.staticId);
  const [busy, setBusy] = useState(false);

  const submit = (): void => {
    setBusy(true);
    apiPatch(`/api/players/${player.userId}`, { gameNick, staticId })
      .then(() => {
        toast.success('Анкета изменена');
        onSaved();
        onClose();
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof Error ? cause.message : 'Не удалось изменить анкету');
        setBusy(false);
      });
  };

  return (
    <Modal
      title="Правка анкеты"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" onClick={submit} busy={busy}>
            Сохранить
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Игровой ник">
          <TextInput
            value={gameNick}
            autoFocus
            onChange={(event) => {
              setGameNick(event.target.value);
            }}
          />
        </Field>
        <Field label="Статик">
          <TextInput
            value={staticId}
            onChange={(event) => {
              setStaticId(event.target.value);
            }}
          />
        </Field>
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
          Ник напечатан в составах наборов в Discord — те сообщения обновятся сами. По статику к анкете подтягиваются
          боевые цифры: поправив его, вы поменяете и их.
        </p>
      </div>
    </Modal>
  );
}

function DeletePlayerModal({
  player,
  onClose,
  onDeleted,
}: {
  player: PlayerRow;
  onClose: () => void;
  onDeleted: () => void;
}): ReactNode {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const submit = (): void => {
    setBusy(true);
    apiDelete<{ keptInEvents: number; rolesRemoved: string[] | null; rolesProblem: string | null }>(
      `/api/players/${player.userId}`,
    )
      .then((result) => {
        const events =
          result.keptInEvents === 0
            ? ''
            : `, записи в ${formatCount(result.keptInEvents, 'наборе', 'наборах', 'наборах')} остались`;

        // Про роли говорим отдельной строкой: снялись они молча, и человек
        // должен знать, что именно ушло — особенно если не ушло ничего.
        if (result.rolesProblem !== null) {
          toast.error(`Анкета удалена${events}, но роли снять не вышло: ${result.rolesProblem}`);
        } else if (result.rolesRemoved !== null && result.rolesRemoved.length > 0) {
          toast.success(`Анкета удалена${events}. Снятые роли: ${result.rolesRemoved.join(', ')}`);
        } else {
          toast.success(`Анкета удалена${events}. Снимать было нечего — ролей не было`);
        }

        onDeleted();
        onClose();
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof Error ? cause.message : 'Не удалось удалить анкету');
        setBusy(false);
      });
  };

  return (
    <Modal
      title="Удалить анкету"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="danger" onClick={submit} busy={busy}>
            Удалить насовсем
          </Button>
        </>
      }
    >
      <p className="text-sm">
        Анкета <strong>{player.gameNick}</strong> ({player.staticId}) будет удалена из базы. Записи в наборах при этом
        останутся, и списки в Discord бот переписывать не станет — в канале человек будет виден в составах как был.
      </p>
      <p className="mt-3 text-sm">
        Заодно с человека <strong>снимутся роли Discord</strong> — все, кроме роли новичка и тех, что боту не
        подвластны. Тир уйдёт вместе с ними.
      </p>
      <p className="mt-3 text-sm" style={{ color: 'var(--text-muted)' }}>
        Человек останется на сервере Discord и сможет заполнить анкету заново — тогда вся его история наборов
        вернётся сама. Роли придётся выдать заново.
      </p>
    </Modal>
  );
}

/** Роль в ответе о снятии: только то, что нужно показать. */
interface RoleView {
  id: string;
  name: string;
}

type KeepReason = 'newcomer' | 'managed' | 'above-bot';

interface KeptRole {
  role: RoleView;
  reason: KeepReason;
}

interface RolesPlan {
  remove: RoleView[];
  keep: KeptRole[];
}

const KEEP_REASON_LABEL: Record<KeepReason, string> = {
  newcomer: 'оставляем намеренно',
  managed: 'роль интеграции, её не снять никому',
  'above-bot': 'выше роли бота, ему не подвластна',
};

function roleNames(roles: readonly RoleView[]): string {
  return roles.map((role) => role.name).join(', ');
}

/**
 * Снятие всех ролей, кроме роли новичка.
 *
 * Список ролей спрашивается у сервера при открытии, а не берётся из строки
 * таблицы: в базе лежит только тир, а снимется всё, что висит на человеке
 * в Discord. Показать надо именно это — иначе подтверждают одно, а
 * снимается другое.
 */
function ClearRolesModal({
  player,
  onClose,
  onCleared,
}: {
  player: PlayerRow;
  onClose: () => void;
  onCleared: () => void;
}): ReactNode {
  const toast = useToast();
  const [plan, setPlan] = useState<RolesPlan | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const abort = new AbortController();
    apiGet<RolesPlan>(`/api/players/${player.userId}/roles`, abort.signal)
      .then(setPlan)
      .catch((cause: unknown) => {
        if (!abort.signal.aborted) {
          setFailure(cause instanceof Error ? cause.message : 'Не удалось прочитать роли');
        }
      });

    return () => {
      abort.abort();
    };
  }, [player.userId]);

  const submit = (): void => {
    setBusy(true);
    apiPost<{ removed: RoleView[] }>(`/api/players/${player.userId}/roles/clear`)
      .then((result) => {
        toast.success(
          result.removed.length === 0
            ? 'Снимать было нечего'
            : `Снято ${formatCount(result.removed.length, 'роль', 'роли', 'ролей')}`,
        );
        onCleared();
        onClose();
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof Error ? cause.message : 'Не удалось снять роли');
        setBusy(false);
      });
  };

  return (
    <Modal
      title="Снять роли"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="danger" onClick={submit} busy={busy} disabled={plan === null || plan.remove.length === 0}>
            Снять роли
          </Button>
        </>
      }
    >
      {failure !== null ? (
        <p className="text-sm" style={{ color: 'var(--danger)' }}>
          {failure}
        </p>
      ) : plan === null ? (
        <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-muted)' }}>
          <Spinner /> Читаю роли в Discord…
        </div>
      ) : (
        <div className="space-y-3 text-sm">
          {plan.remove.length === 0 ? (
            <p>
              С <strong>{player.gameNick}</strong> снимать нечего: подвластных боту ролей на нём нет.
            </p>
          ) : (
            <p>
              С <strong>{player.gameNick}</strong> ({player.staticId}) снимется{' '}
              {formatCount(plan.remove.length, 'роль', 'роли', 'ролей')}: <strong>{roleNames(plan.remove)}</strong>.
            </p>
          )}

          {plan.keep.length > 0 && (
            <div>
              <p style={{ color: 'var(--text-muted)' }}>Останутся:</p>
              <ul className="mt-1 space-y-0.5">
                {plan.keep.map((kept) => (
                  <li key={kept.role.id} style={{ color: 'var(--text-muted)' }}>
                    <strong>{kept.role.name}</strong> — {KEEP_REASON_LABEL[kept.reason]}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p style={{ color: 'var(--text-muted)' }}>
            Вернуть роли бот не умеет — снятое записывается в журнал поимённо, оттуда и восстанавливают. Анкета и
            статистика остаются на месте.
          </p>
        </div>
      )}
    </Modal>
  );
}

export function PlayersPage({ user, timezone }: { user: SessionUser; timezone: string }): ReactNode {
  const toast = useToast();
  const { data, loading, error, reload } = useResource<PlayersResponse>(
    '/api/players',
    (event) => event.type === 'players.changed',
  );

  /*
   * Список тех, у кого тир без анкеты, подтягивается только когда его просят.
   *
   * Он собирается запросом ко всем участникам Discord и потому работает
   * лишь при подключённом боте, а список игроков обязан открываться и без
   * него: он читается из базы. Поэтому запрос отдельный и по требованию, а
   * не частью `/api/players`.
   */
  const [scope, setScope] = useState<ProfileScope>('with');
  const strays = useResource<StraysResponse>(scope === 'with' ? null : '/api/players/without-profile');

  // Отбор и сортировка берутся из адреса: по ссылке страница открывается в
  // том же виде, в каком её отправили, а обновление не сбрасывает выбранное.
  const initialView = useMemo(() => currentPlayerView(), []);

  const [filters, setFilters] = useState<PlayerFilters>(initialView.filters);
  const [visibleColumns, setVisibleColumns] = useState<PlayerSortKey[]>(readStoredColumns);
  const [columnsOpen, setColumnsOpen] = useState(false);
  // По тиру от первого к четвёртому: тир — первое, по чему список читают.
  const [sortKey, setSortKey] = useState<PlayerSortKey>(initialView.sortKey);
  const [ascending, setAscending] = useState(initialView.ascending);
  const [editing, setEditing] = useState<PlayerRow | null>(null);
  const [removing, setRemoving] = useState<PlayerRow | null>(null);
  const [clearingRoles, setClearingRoles] = useState<PlayerRow | null>(null);

  // Адрес переписывается заменой, а не переходом: иначе каждая буква,
  // набранная в поиске, добавляла бы шаг в историю, и «назад» вместо возврата
  // на прошлую страницу отматывало бы поиск по одной букве.
  useEffect(() => {
    const search = playerViewToQuery({ filters, sortKey, ascending });
    if (search !== window.location.search) {
      window.history.replaceState({}, '', `${window.location.pathname}${search}`);
    }
  }, [filters, sortKey, ascending]);

  const [refreshing, setRefreshing] = useState(false);
  const canEdit = hasCapability(user.level, 'players.edit');
  const canDelete = hasCapability(user.level, 'players.delete');
  const canClearRoles = hasCapability(user.level, 'players.roles');

  // Тиры хранятся в базе и обновляются сами по событию смены ролей.
  // Кнопка нужна на случай, когда бот был выключен в момент правки.
  const refreshTiers = (): void => {
    setRefreshing(true);
    apiPost<{ checked: number; changed: number }>('/api/players/tiers/refresh')
      .then((result) => {
        toast.success(
          result.changed === 0
            ? `Тиры сверены, изменений нет (проверено ${String(result.checked)})`
            : `Тиры обновлены: изменилось ${String(result.changed)} из ${String(result.checked)}`,
        );
        reload();
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof Error ? cause.message : 'Не удалось обновить тиры');
      })
      .finally(() => {
        setRefreshing(false);
      });
  };

  const activity: CaptureActivity = data?.activity ?? { week: 0, month: 0 };
  const columnContext: ColumnContext = { activity, timezone };

  // Анкеты и боевые цифры сшиваются один раз на ответ, а не в каждой строке:
  // сортировка и отбор дальше работают с готовой строкой.
  const profiles = useMemo<PlayerRow[]>(
    () => (data?.players ?? []).map((player) => ({ ...player, combat: data?.combat[player.userId] ?? null })),
    [data],
  );

  /*
   * Строки для тех, у кого тир есть, а анкеты нет.
   *
   * Они встают в тот же список, а не в отдельную вкладку: на них надо
   * действовать теми же кнопками — сменить тир, снять роли, — а не смотреть
   * со стороны. Анкеты у них нет вовсе, поэтому ник и статик пустые, а цифр
   * по каптам не существует: в состав такой человек не попадал.
   */
  const strayRows = useMemo<PlayerRow[]>(
    () =>
      (strays.data?.members ?? []).map((member) => ({
        userId: member.userId,
        gameNick: '',
        staticId: '',
        mclVzzCount: 0,
        captureCount: 0,
        mainCount: 0,
        reserveCount: 0,
        tier: member.tier,
        discordName: member.discordName,
        avatarUrl: member.avatarUrl,
        leftAt: null,
        // Вместо «когда обновлена анкета» — когда человек пришёл на сервер:
        // анкеты нет, обновляться нечему.
        updatedAt: member.joinedAt ?? '',
        combat: null,
      })),
    [strays.data],
  );

  const strayIds = useMemo(() => new Set(strayRows.map((row) => row.userId)), [strayRows]);
  // Число рядом с подписью появляется, только когда список уже приехал: до
  // того «0» читался бы как «никого нет», а это ещё неизвестно.
  const strayCount = strays.data === null ? '' : ` (${String(strays.data.members.length)})`;
  const rows = useMemo(
    () => (scope === 'without' ? strayRows : scope === 'all' ? [...profiles, ...strayRows] : profiles),
    [scope, profiles, strayRows],
  );

  const shown = useMemo(
    () => filterPlayers(rows, filters).sort((left, right) => comparePlayers(left, right, sortKey, ascending)),
    [rows, filters, sortKey, ascending],
  );

  const columns = OPTIONAL_COLUMNS.filter((column) => visibleColumns.includes(column.key));
  const departed = rows.filter((player) => player.leftAt !== null).length;
  const filtered = isFilterActive(filters);

  const toggleColumn = (key: PlayerSortKey): void => {
    setVisibleColumns((current) => {
      const next = current.includes(key) ? current.filter((item) => item !== key) : [...current, key];
      storeColumns(next);
      return next;
    });
  };

  const toggleSort = (key: PlayerSortKey): void => {
    if (key === sortKey) {
      setAscending((value) => !value);
      return;
    }

    setSortKey(key);
    // Числа интереснее сверху вниз, имена — по алфавиту, тир — от первого
    // к третьему: сильнейшие сверху, как их и привыкли читать.
    setAscending(key === 'gameNick' || key === 'staticId' || key === 'tier');
  };

  const patch = (change: Partial<PlayerFilters>): void => {
    setFilters((current) => ({ ...current, ...change }));
  };

  const toggleTier = (tier: Tier): void => {
    patch({ tiers: filters.tiers.includes(tier) ? filters.tiers.filter((item) => item !== tier) : [...filters.tiers, tier] });
  };

  const toggleProblem = (problem: PlayerProblem): void => {
    patch({
      problems: filters.problems.includes(problem)
        ? filters.problems.filter((item) => item !== problem)
        : [...filters.problems, problem],
    });
  };

  const sortableHeader = (key: PlayerSortKey, label: string, numeric: boolean, title?: string): ReactNode => (
    <th
      key={key}
      className={`cursor-pointer select-none ${numeric ? 'text-right' : 'text-left'}`}
      style={sortKey === key ? { color: 'var(--accent)' } : undefined}
      title={title}
      onClick={() => {
        toggleSort(key);
      }}
      aria-sort={sortKey === key ? (ascending ? 'ascending' : 'descending') : 'none'}
    >
      {label}
      {sortKey === key && <span aria-hidden="true"> {ascending ? '↑' : '↓'}</span>}
    </th>
  );

  if (error !== null) {
    return <ErrorState message={error} onRetry={reload} />;
  }

  return (
    <>
      <PageHeader
        title="Игроки"
        description={
          data === null
            ? undefined
            : [
                formatCount(rows.length, 'анкета', 'анкеты', 'анкет'),
                shown.length === rows.length ? null : `показано ${String(shown.length)}`,
                departed === 0 ? null : `${String(departed)} вышли с сервера`,
              ]
                .filter((part) => part !== null)
                .join(' · ')
        }
        actions={
          <>
            <Button
              variant="secondary"
              busy={refreshing}
              disabled={data !== null && !data.botReady}
              title={data !== null && !data.botReady ? 'Бот не подключён к Discord' : 'Сверить тиры с ролями Discord'}
              onClick={refreshTiers}
            >
              Обновить тиры
            </Button>
            <Button
              variant="secondary"
              disabled={shown.length === 0}
              title="Выгружает то, что сейчас отобрано, и только включённые колонки"
              onClick={() => {
                downloadCsv(shown, columns, columnContext);
              }}
            >
              Выгрузить CSV
            </Button>
          </>
        }
      />

      {data !== null && (
        <p className="mb-3 text-xs" style={{ color: 'var(--text-muted)' }}>
          {data.tiersCheckedAt === null
            ? 'Тиры ещё ни разу не сверялись с ролями Discord.'
            : `Тиры сверены ${formatRelative(data.tiersCheckedAt)} · ${formatDateTime(data.tiersCheckedAt, timezone)}`}
          {data.botReady ? ' · обновляются сами при смене ролей' : ' · бот отключён, обновление недоступно'}
          {activity.month > 0 && ` · каптов за месяц: ${String(activity.month)}, за неделю: ${String(activity.week)}`}
        </p>
      )}

      <Panel className="mb-4 p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-64 flex-1">
            <Field label="Поиск">
              <TextInput
                type="search"
                value={filters.query}
                placeholder="Ник, статик или имя в Discord"
                onChange={(event) => {
                  patch({ query: event.target.value });
                }}
              />
            </Field>
          </div>

          <Field label="Кто в списке">
            <SegmentedControl<PlayerStatus>
              label="Статус"
              value={filters.status}
              onChange={(value) => {
                patch({ status: value });
              }}
              options={[
                { value: 'all', label: 'Все' },
                { value: 'present', label: 'На сервере' },
                { value: 'left', label: 'Вышли' },
              ]}
            />
          </Field>

          {/*
            Отдельный выбор, а не галка: он не сужает список, а меняет, из
            чего тот вообще собран. «Чепушилы» — люди с тир-ролью, но без
            анкеты; их берут из Discord, а не из базы, и чаще всего смотреть
            их удобнее отдельно от всех.
          */}
          <Field label="Анкета">
            <SegmentedControl<ProfileScope>
              label="Анкета"
              value={scope}
              onChange={setScope}
              options={[
                { value: 'with', label: 'Есть' },
                { value: 'without', label: `Чепушилы${strayCount}` },
                { value: 'all', label: 'Вместе' },
              ]}
            />
          </Field>

          <Field label="Был на капте">
            <SegmentedControl<PlayerActivity>
              label="Активность"
              value={filters.activity}
              onChange={(value) => {
                patch({ activity: value });
              }}
              options={[
                { value: 'all', label: 'Когда угодно' },
                { value: 'week', label: 'За неделю' },
                { value: 'month', label: 'За месяц' },
                { value: 'stale', label: 'Давно не был' },
              ]}
            />
          </Field>
        </div>

        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div>
            <span className="mb-1.5 block text-sm font-medium">Тир</span>
            <div className="flex flex-wrap gap-3">
              {TIER_OPTIONS.map((option) => (
                <div key={String(option.value)} className={FILTER_CELL}>
                  <FilterChip
                    label={option.label}
                    color={tierColor(option.value)}
                    active={filters.tiers.includes(option.value)}
                    onSelect={() => {
                      toggleTier(option.value);
                    }}
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <span className="mb-1.5 block text-sm font-medium">Разобрать</span>
            <div className="flex flex-wrap gap-3">
              {PROBLEM_OPTIONS.map((option) => (
                <div key={option.value} className={FILTER_CELL}>
                  <FilterChip
                    label={option.label}
                    active={filters.problems.includes(option.value)}
                    title={option.hint}
                    onSelect={() => {
                      toggleProblem(option.value);
                    }}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className={FILTER_CELL}>
            <ThresholdInput
              label="Каптов от"
              value={filters.minCaptures}
              onChange={(value) => {
                patch({ minCaptures: value });
              }}
            />
          </div>
          <div className={FILTER_CELL}>
            <ThresholdInput
              label="Урон/капт от"
              value={filters.minAvgDamage}
              step="50"
              onChange={(value) => {
                patch({ minAvgDamage: value });
              }}
            />
          </div>
          <div className={FILTER_CELL}>
            <ThresholdInput
              label="Киллы/капт от"
              value={filters.minAvgKills}
              step="0.5"
              onChange={(value) => {
                patch({ minAvgKills: value });
              }}
            />
          </div>

          <div className="ml-auto flex items-center gap-2">
            <Button
              size="sm"
              onClick={() => {
                setColumnsOpen((open) => !open);
              }}
              aria-expanded={columnsOpen}
            >
              Колонки ({String(columns.length)})
            </Button>
            {filtered && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setFilters(EMPTY_FILTERS);
                }}
              >
                Сбросить
              </Button>
            )}
          </div>
        </div>

        {columnsOpen && (
          <div className="mt-4 flex flex-wrap gap-6 border-t pt-4" style={{ borderColor: 'var(--border-subtle)' }}>
            {['Наборы', 'Капты'].map((group) => (
              <div key={group}>
                <span className="mb-2 block text-xs font-medium" style={{ color: 'var(--text-muted)' }}>
                  {group}
                </span>
                <div className="space-y-1.5">
                  {OPTIONAL_COLUMNS.filter((column) => column.group === group).map((column) => (
                    <label key={column.key} className="flex cursor-pointer items-center gap-2 text-sm" title={column.hint}>
                      <Checkbox
                        checked={visibleColumns.includes(column.key)}
                        onChange={() => {
                          toggleColumn(column.key);
                        }}
                      />
                      {column.label}
                    </label>
                  ))}
                </div>
              </div>
            ))}
            <p className="max-w-xs text-xs" style={{ color: 'var(--text-muted)' }}>
              «Наборы» — записи в наборы Discord, «Капты» — настоящие капты по данным игровой службы. Одно с другим не
              совпадает: на капт можно записаться и не выйти, а выйти можно и без записи.
            </p>
          </div>
        )}
      </Panel>

      {/*
        Отказ показывается строкой, а не пустотой: список чепушил собирается
        запросом ко всем участникам Discord, и без бота его не собрать —
        промолчать значило бы соврать, что таких нет.
      */}
      {scope !== 'with' && strays.error !== null && (
        <p className="mb-4 text-sm" style={{ color: 'var(--danger)' }}>
          Не удалось собрать список тех, у кого тир без анкеты: {strays.error}
        </p>
      )}

      <Panel className="overflow-hidden">
        {/* Пока едет список чепушил, показываем скелет, а не «ничего не
            найдено»: пустой ответ ещё не пришёл, и говорить о нём рано. */}
        {loading || data === null || (scope !== 'with' && strays.loading) ? (
          <Skeleton rows={8} />
        ) : shown.length === 0 ? (
          <EmptyState
            title="Ничего не найдено"
            description={rows.length === 0 ? 'Пока никто не заполнил анкету.' : 'Попробуйте ослабить отбор.'}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table min-w-[760px]">
              <thead>
                <tr>
                  <th>Discord</th>
                  {sortableHeader('tier', 'Тир', false)}
                  {sortableHeader('gameNick', 'Ник', false)}
                  {sortableHeader('staticId', 'Статик', false)}
                  {columns.map((column) => sortableHeader(column.key, column.label, true, column.hint))}
                  <th aria-label="Статистика" />
                  {sortableHeader('updatedAt', 'Обновлена', false)}
                  {(canEdit || canClearRoles) && <th aria-label="Действия" />}
                </tr>
              </thead>
              <tbody>
                {shown.map((player) => {
                  // Строка без анкеты: править и удалять у неё нечего, а
                  // карточка статистики пуста — человек не был ни на одном
                  // капте, потому что в состав его без анкеты не записать.
                  const stray = strayIds.has(player.userId);

                  return (
                  <tr key={player.userId}>
                    {/* Пока бот ни разу не видел человека, имени нет —
                        показываем идентификатор, а не пустую клетку. */}
                    <td
                      className={player.discordName === null ? 'font-mono text-xs' : 'text-sm'}
                      style={{ color: 'var(--text-muted)' }}
                      title={player.userId}
                    >
                      {player.discordName ?? player.userId}
                    </td>
                    <td>
                      {canClearRoles && player.leftAt === null ? (
                        <TierPicker userId={player.userId} tier={player.tier} />
                      ) : (
                        <TierBadge tier={player.tier} />
                      )}
                    </td>
                    <td className="font-medium">
                      {stray ? (
                        <Badge tone="warning">без анкеты</Badge>
                      ) : (
                        <span className={player.leftAt === null ? '' : 'line-through opacity-60'}>
                          {player.gameNick}
                        </span>
                      )}
                      {player.leftAt !== null && (
                        <span className="ml-2 align-middle">
                          <Badge tone="danger">вышел</Badge>
                        </span>
                      )}
                    </td>
                    <td>{player.staticId}</td>
                    {columns.map((column) => (
                      <td key={column.key} className="text-right tabular-nums">
                        {column.value(player, columnContext)}
                      </td>
                    ))}
                    <td>
                      {!stray && (
                        <Button
                          size="sm"
                          onClick={() => {
                            navigate(`/players/${player.userId}`);
                          }}
                        >
                          Статистика
                        </Button>
                      )}
                    </td>
                    <td className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      {stray
                        ? player.updatedAt === ''
                          ? 'на сервере неизвестно с каких пор'
                          : `на сервере с ${formatRelative(player.updatedAt)}`
                        : player.leftAt === null
                          ? formatDateTime(player.updatedAt, timezone)
                          : `вышел ${formatRelative(player.leftAt)}`}
                    </td>
                    {(canEdit || canClearRoles) && (
                      <td>
                        <div className="flex justify-end gap-1.5">
                          {canEdit && !stray && (
                            <Button
                              size="sm"
                              onClick={() => {
                                setEditing(player);
                              }}
                            >
                              Править
                            </Button>
                          )}
                          {canClearRoles && (
                            <Button
                              size="sm"
                              variant="danger"
                              disabled={player.leftAt !== null}
                              title={
                                player.leftAt === null
                                  ? 'Снять все роли, кроме роли новичка'
                                  : 'Человека нет на сервере — снимать нечего'
                              }
                              onClick={() => {
                                setClearingRoles(player);
                              }}
                            >
                              Снять роли
                            </Button>
                          )}
                          {canDelete && !stray && (
                            <Button
                              size="sm"
                              variant="ghost"
                              aria-label={`Удалить анкету ${player.gameNick}`}
                              onClick={() => {
                                setRemoving(player);
                              }}
                            >
                              ✕
                            </Button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {editing !== null && (
        <EditPlayerModal
          player={editing}
          onClose={() => {
            setEditing(null);
          }}
          onSaved={reload}
        />
      )}

      {removing !== null && (
        <DeletePlayerModal
          player={removing}
          onClose={() => {
            setRemoving(null);
          }}
          onDeleted={reload}
        />
      )}

      {clearingRoles !== null && (
        <ClearRolesModal
          player={clearingRoles}
          onClose={() => {
            setClearingRoles(null);
          }}
          onCleared={reload}
        />
      )}
    </>
  );
}
